/**
 * Staff Permissions Settings Tab Component
 * Handles role-based permissions, templates, and role editing
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Shield,
  Users,
  Clock,
  FileText,
  Mail,
  MapPin,
  Check,
  Edit,
  RotateCcw,
  Settings,
  Trash2,
  Plus,
  Zap,
  AlertCircle,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import EmployeePermissionsPanel from "@/components/EmployeePermissionsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Built-in permission templates
const PERMISSION_TEMPLATES = {
  full_access: {
    name: "Full Access",
    description: "All permissions enabled (except admin-only)",
    icon: "👑",
    color: "purple",
    permissions: {
      clients: ["create", "read", "update", "delete"],
      appointments: ["create", "read", "update", "delete"],
      reports: ["read", "export"],
      evv: ["clock_in", "clock_out", "read", "update"],
      visit_notes: ["create", "read", "update"],
      communications: ["read", "send"],
      referrals: ["create", "read", "update"],
    },
  },
  read_only: {
    name: "Read Only",
    description: "View access to most features, no editing",
    icon: "👁️",
    color: "slate",
    permissions: {
      clients: ["read"],
      appointments: ["read"],
      reports: ["read"],
      evv: ["read"],
      visit_notes: ["read"],
      communications: ["read"],
    },
  },
  clinical_staff: {
    name: "Clinical Staff",
    description: "For RNs and clinical supervisors",
    icon: "🏥",
    color: "blue",
    permissions: {
      clients: ["read", "update"],
      appointments: ["create", "read", "update"],
      reports: ["read", "export"],
      evv: ["read", "update"],
      visit_notes: ["create", "read", "update"],
      communications: ["read", "send"],
      evaluations: ["create", "read", "update"],
    },
  },
  field_worker: {
    name: "Field Worker",
    description: "For PCWs and caregivers in the field",
    icon: "🚗",
    color: "emerald",
    permissions: {
      clients: ["read"],
      appointments: ["read"],
      evv: ["clock_in", "clock_out", "read"],
      visit_notes: ["create", "read"],
      communications: ["read"],
    },
  },
  office_staff: {
    name: "Office Staff",
    description: "For receptionists and office coordinators",
    icon: "📋",
    color: "amber",
    permissions: {
      clients: ["read", "update"],
      appointments: ["create", "read", "update"],
      communications: ["read", "send"],
      referrals: ["read"],
    },
  },
  intake_specialist: {
    name: "Intake Specialist",
    description: "Focused on referrals and new clients",
    icon: "📝",
    color: "pink",
    permissions: {
      clients: ["create", "read", "update"],
      referrals: ["create", "read", "update"],
      communications: ["read", "send"],
      appointments: ["create", "read"],
    },
  },
};

const TEMPLATE_ICONS = ["⚙️", "🔧", "🛡️", "🎯", "⭐", "🔑", "📊", "💼", "🏥", "👥", "📝", "✨"];
const TEMPLATE_COLORS = [
  { id: "purple", label: "Purple", class: "bg-purple-100 border-purple-300" },
  { id: "slate", label: "Slate", class: "bg-slate-100 border-slate-300" },
  { id: "blue", label: "Blue", class: "bg-blue-100 border-blue-300" },
  { id: "emerald", label: "Emerald", class: "bg-emerald-100 border-emerald-300" },
  { id: "amber", label: "Amber", class: "bg-amber-100 border-amber-300" },
  { id: "pink", label: "Pink", class: "bg-pink-100 border-pink-300" },
  { id: "red", label: "Red", class: "bg-red-100 border-red-300" },
  { id: "cyan", label: "Cyan", class: "bg-cyan-100 border-cyan-300" },
];

const PERMISSION_CATEGORIES = {
  clients: { label: "Clients", actions: ["create", "read", "update", "delete"] },
  appointments: { label: "Appointments", actions: ["create", "read", "update", "delete"] },
  evv: { label: "EVV/Time", actions: ["clock_in", "clock_out", "read", "update"] },
  visit_notes: { label: "Visit Notes", actions: ["create", "read", "update"] },
  communications: { label: "Communications", actions: ["read", "send"] },
  referrals: { label: "Referrals", actions: ["create", "read", "update"] },
  reports: { label: "Reports", actions: ["read", "export"] },
  evaluations: { label: "Evaluations", actions: ["create", "read", "update"] },
  documents: { label: "Documents / Files", actions: ["read", "download", "upload", "delete"] },
};

const formatRoleName = (role) => {
  const names = {
    admin: "Administrator",
    case_manager: "Case Manager",
    rn: "Registered Nurse",
    pcw: "Personal Care Worker",
    intake_coordinator: "Intake Coordinator",
    receptionist: "Receptionist",
  };
  return names[role] || role;
};

export function StaffPermissionsTab() {
  // State
  const [permissions, setPermissions] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [showComparisonMatrix, setShowComparisonMatrix] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  
  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    icon: "⚙️",
    color: "slate",
    permissions: {},
  });

  // Per-employee overrides
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
    fetchCustomTemplates();
    fetchEmployeesForOverrides();
  }, []);

  const fetchEmployeesForOverrides = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      const list = (res.data?.users || []).filter((u) => u.role !== "admin" && u.is_active !== false);
      setEmployees(list);
    } catch (err) {
      console.error("Failed to fetch employees for permission overrides", err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      const roles = response.data.roles || {};
      const flattenedPermissions = {};
      for (const [roleId, roleData] of Object.entries(roles)) {
        flattenedPermissions[roleId] = roleData.permissions || roleData;
      }
      setPermissions({ permissions: flattenedPermissions });
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await axios.get(`${API}/permission-templates`);
      setCustomTemplates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch custom templates:", error);
    }
  };

  const handleSavePermissions = async (role, newPermissions) => {
    setSavingPermissions(true);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: newPermissions });
      toast.success(`Permissions updated for ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleApplyTemplate = async (role, templateId) => {
    const template = PERMISSION_TEMPLATES[templateId];
    if (!template) return;
    
    if (!confirm(`Apply "${template.name}" template to ${formatRoleName(role)}? This will replace all current permissions.`)) {
      return;
    }
    
    setApplyingTemplate(role);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: template.permissions });
      toast.success(`"${template.name}" template applied to ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleApplyCustomTemplate = async (role, template) => {
    if (!confirm(`Apply "${template.name}" template to ${formatRoleName(role)}? This will replace all current permissions.`)) {
      return;
    }
    
    setApplyingTemplate(role);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: template.permissions });
      toast.success(`"${template.name}" template applied to ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (Object.keys(newTemplate.permissions).length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    setSavingTemplate(true);
    try {
      await axios.post(`${API}/permission-templates`, newTemplate);
      toast.success(`Template "${newTemplate.name}" created successfully`);
      fetchCustomTemplates();
      setShowCreateTemplate(false);
      setNewTemplate({ name: "", description: "", icon: "⚙️", color: "slate", permissions: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/permission-templates/${editingTemplate.id}`, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        icon: editingTemplate.icon,
        color: editingTemplate.color,
        permissions: editingTemplate.permissions
      });
      toast.success(`Template "${editingTemplate.name}" updated`);
      fetchCustomTemplates();
      setEditingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/permission-templates/${templateId}`);
      toast.success(`Template "${templateName}" deleted`);
      fetchCustomTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const toggleTemplatePermission = (category, action, isEditing = false) => {
    const targetTemplate = isEditing ? editingTemplate : newTemplate;
    const setTemplate = isEditing ? setEditingTemplate : setNewTemplate;
    
    const currentPerms = { ...targetTemplate.permissions };
    if (!currentPerms[category]) {
      currentPerms[category] = [];
    }
    
    if (currentPerms[category].includes(action)) {
      currentPerms[category] = currentPerms[category].filter(a => a !== action);
      if (currentPerms[category].length === 0) {
        delete currentPerms[category];
      }
    } else {
      currentPerms[category] = [...currentPerms[category], action];
    }
    
    setTemplate({ ...targetTemplate, permissions: currentPerms });
  };

  const handleResetPermissions = async () => {
    if (!confirm("Reset all role permissions to defaults? This cannot be undone.")) return;
    
    try {
      const roles = ["case_manager", "rn", "intake_coordinator", "receptionist"];
      for (const role of roles) {
        await axios.post(`${API}/roles/reset/${role}`);
      }
      toast.success("Permissions reset to defaults");
      fetchPermissions();
    } catch (error) {
      toast.error("Failed to reset permissions");
    }
  };

  const getColorClass = (color) => {
    const colorMap = {
      purple: "border-purple-200 bg-purple-50 hover:border-purple-400",
      slate: "border-slate-200 bg-slate-50 hover:border-slate-400",
      blue: "border-blue-200 bg-blue-50 hover:border-blue-400",
      emerald: "border-emerald-200 bg-emerald-50 hover:border-emerald-400",
      amber: "border-amber-200 bg-amber-50 hover:border-amber-400",
      pink: "border-pink-200 bg-pink-50 hover:border-pink-400",
      red: "border-red-200 bg-red-50 hover:border-red-400",
      cyan: "border-cyan-200 bg-cyan-50 hover:border-cyan-400",
    };
    return colorMap[color] || colorMap.slate;
  };

  return (
    <div className="space-y-6" data-testid="permissions-settings-tab">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Staff Permissions
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Control what portal users can access and do in the system
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button 
              size="sm"
              onClick={() => setShowCreateTemplate(true)}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-template-btn-header"
            >
              <Plus className="w-4 h-4" />
              Create Custom Template
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowComparisonMatrix(true)}
              className="gap-2"
              data-testid="compare-permissions-btn"
            >
              <Users className="w-4 h-4" />
              Compare All Roles
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleResetPermissions}
              className="gap-2 text-amber-600 hover:text-amber-700"
              data-testid="reset-permissions-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </Button>
          </div>
        </div>

        {/* Info about PCW */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Personal Care Workers (PCWs) do not have access to this portal. 
            They are managed through the Employees section and use EVV for clock in/out.
          </p>
        </div>

        {/* Quick Legend */}
        <div className="flex flex-wrap gap-4 p-3 bg-slate-50 rounded-lg text-xs">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-emerald-500"></span>
            <span className="text-slate-600">Permitted</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-slate-300"></span>
            <span className="text-slate-600">Not Permitted</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-amber-600">*</span>
            <span className="text-slate-600">Customized from default</span>
          </div>
        </div>
      </div>

      {/* Permission Templates Card */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6" data-testid="permission-templates-card">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-slate-900">Quick Apply: Permission Templates</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Use pre-configured templates to quickly set up permissions for any role. Click a template, then select which role to apply it to.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(PERMISSION_TEMPLATES).map(([templateId, template]) => (
            <div 
              key={templateId}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${getColorClass(template.color)}`}
              data-testid={`template-${templateId}`}
            >
              <div className="text-2xl mb-1">{template.icon}</div>
              <h4 className="font-medium text-sm text-slate-900">{template.name}</h4>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
              <Select onValueChange={(role) => handleApplyTemplate(role, templateId)}>
                <SelectTrigger className="mt-2 h-7 text-xs">
                  <SelectValue placeholder="Apply to..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rn">RN</SelectItem>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                  <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                  <SelectItem value="intake_coordinator">Intake Coord.</SelectItem>
                  <SelectItem value="receptionist">Receptionist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Templates Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6" data-testid="custom-templates-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Your Custom Templates</h3>
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
              {customTemplates.length} saved
            </span>
          </div>
          <Button 
            size="sm"
            onClick={() => setShowCreateTemplate(true)}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4" />
            Create Template
          </Button>
        </div>
        
        {customTemplates.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Settings className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No custom templates yet</p>
            <p className="text-xs text-slate-400 mt-1">Create your first template to save time configuring permissions</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {customTemplates.map((template) => (
              <div 
                key={template.id}
                className={`p-3 rounded-lg border-2 transition-all hover:shadow-md relative ${getColorClass(template.color)}`}
                data-testid={`custom-template-${template.id}`}
              >
                {/* Edit/Delete buttons */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => setEditingTemplate(template)}
                    data-testid={`edit-custom-template-${template.id}`}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                    onClick={() => handleDeleteTemplate(template.id, template.name)}
                    data-testid={`delete-custom-template-${template.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                
                <div className="text-2xl mb-1">{template.icon}</div>
                <h4 className="font-medium text-sm text-slate-900 pr-12">{template.name}</h4>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description || "Custom template"}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {Object.keys(template.permissions || {}).length} categories
                </p>
                <Select onValueChange={(role) => handleApplyCustomTemplate(role, template)}>
                  <SelectTrigger className="mt-2 h-7 text-xs">
                    <SelectValue placeholder="Apply to..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rn">RN</SelectItem>
                    <SelectItem value="case_manager">Case Manager</SelectItem>
                    <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                    <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                    <SelectItem value="intake_coordinator">Intake Coord.</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Role Permission Cards */}
      {permissions && (
        <>
          {/* RN Card */}
          {permissions.permissions?.rn && (
            <RolePermissionCard
              role="rn"
              title="Registered Nurse (RN)"
              description="Clinical oversight, evaluations, and supervision"
              icon={<Shield className="w-5 h-5 text-blue-600" />}
              permissions={permissions.permissions.rn}
              editingRole={editingRole}
              setEditingRole={setEditingRole}
              onSavePermissions={handleSavePermissions}
              bgColor="border-blue-200"
            />
          )}

          {/* Case Manager Card */}
          {permissions.permissions?.case_manager && (
            <RolePermissionCard
              role="case_manager"
              title="Case Manager"
              description="Client cases and care coordination"
              icon={<FileText className="w-5 h-5 text-emerald-600" />}
              permissions={permissions.permissions.case_manager}
              editingRole={editingRole}
              setEditingRole={setEditingRole}
              onSavePermissions={handleSavePermissions}
              bgColor="border-emerald-200"
            />
          )}

          {/* HR/Scheduler Card */}
          {permissions.permissions?.hr_scheduler && (
            <RolePermissionCard
              role="hr_scheduler"
              title="HR/Scheduler"
              description="HR functions, payroll, and employee scheduling"
              icon={<Calendar className="w-5 h-5 text-purple-600" />}
              permissions={permissions.permissions.hr_scheduler}
              editingRole={editingRole}
              setEditingRole={setEditingRole}
              onSavePermissions={handleSavePermissions}
              bgColor="border-purple-200"
            />
          )}

          {/* EVV Specialist Card */}
          {permissions.permissions?.evv_specialist && (
            <RolePermissionCard
              role="evv_specialist"
              title="EVV Specialist"
              description="Electronic Visit Verification and time tracking"
              icon={<Clock className="w-5 h-5 text-cyan-600" />}
              permissions={permissions.permissions.evv_specialist}
              editingRole={editingRole}
              setEditingRole={setEditingRole}
              onSavePermissions={handleSavePermissions}
              bgColor="border-cyan-200"
            />
          )}

          {/* Other Roles */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Other Staff Roles</h3>
            <div className="space-y-4">
              {/* Intake Coordinator */}
              {permissions.permissions?.intake_coordinator && (
                <CompactRoleCard
                  role="intake_coordinator"
                  title="Intake Coordinator"
                  description="Referrals & new client intake"
                  permissions={permissions.permissions.intake_coordinator}
                  editingRole={editingRole}
                  setEditingRole={setEditingRole}
                  onSavePermissions={handleSavePermissions}
                  colorClass="border-amber-200"
                  badgeClass="bg-amber-100 text-amber-700"
                />
              )}

              {/* Receptionist */}
              {permissions.permissions?.receptionist && (
                <CompactRoleCard
                  role="receptionist"
                  title="Receptionist"
                  description="Front desk & scheduling support"
                  permissions={permissions.permissions.receptionist}
                  editingRole={editingRole}
                  setEditingRole={setEditingRole}
                  onSavePermissions={handleSavePermissions}
                  colorClass="border-pink-200"
                  badgeClass="bg-pink-100 text-pink-700"
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Per-Employee Overrides Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="per-employee-overrides-section">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-600" />
            Per-Employee Overrides
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Pick an employee to grant or revoke specific permissions on top of their role&apos;s baseline.
          </p>
        </div>
        <div className="max-w-md mb-4">
          <Label className="text-xs font-medium text-slate-600 mb-1 block">Employee</Label>
          <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
            <SelectTrigger data-testid="per-employee-picker">
              <SelectValue placeholder="Select an employee…" />
            </SelectTrigger>
            <SelectContent>
              {employees.length === 0 && (
                <div className="px-3 py-2 text-sm text-slate-500">No employees to configure</div>
              )}
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{e.name}</span>
                    <span className="text-xs text-slate-500 capitalize">
                      · {(e.role || "").replace(/_/g, " ")}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedEmployeeId ? (
          <EmployeePermissionsPanel userId={selectedEmployeeId} canEdit />
        ) : (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            Select an employee above to view and edit their permissions.
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">About Staff Permissions</h4>
            <ul className="text-sm text-blue-800 mt-1 space-y-1">
              <li>• <strong>RN</strong>: Registered Nurses handle clinical oversight, evaluations, and supervision of care.</li>
              <li>• <strong>Case Manager</strong>: Coordinates client cases, manages care plans, and handles reporting.</li>
              <li>• <strong>Intake Coordinator</strong>: Handles new client intake, referrals, and communications.</li>
              <li>• <strong>Receptionist</strong>: Front desk operations, scheduling support, and basic client access.</li>
              <li>• Changes take effect immediately for all users with that role.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create Template Dialog */}
      <TemplateDialog
        open={showCreateTemplate}
        onOpenChange={setShowCreateTemplate}
        template={newTemplate}
        setTemplate={setNewTemplate}
        onSave={handleCreateTemplate}
        saving={savingTemplate}
        togglePermission={(cat, act) => toggleTemplatePermission(cat, act, false)}
        isEditing={false}
      />

      {/* Edit Template Dialog */}
      <TemplateDialog
        open={!!editingTemplate}
        onOpenChange={(open) => !open && setEditingTemplate(null)}
        template={editingTemplate}
        setTemplate={setEditingTemplate}
        onSave={handleUpdateTemplate}
        saving={savingTemplate}
        togglePermission={(cat, act) => toggleTemplatePermission(cat, act, true)}
        isEditing={true}
      />

      {/* Comparison Matrix Dialog */}
      <ComparisonMatrixDialog
        open={showComparisonMatrix}
        onOpenChange={setShowComparisonMatrix}
        permissions={permissions}
      />
    </div>
  );
}

// Role Permission Card Component
function RolePermissionCard({ 
  role, 
  title, 
  description, 
  icon, 
  permissions, 
  editingRole, 
  setEditingRole, 
  onSavePermissions,
  bgColor = "border-slate-200" 
}) {
  const isEditing = editingRole === role;

  return (
    <div className={`bg-white rounded-xl border-2 ${bgColor} p-6`} data-testid={`${role}-permissions-card`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        </div>
        <Button 
          variant={isEditing ? "default" : "outline"}
          size="sm" 
          onClick={() => setEditingRole(isEditing ? null : role)}
          data-testid={`edit-${role}-permissions-btn`}
        >
          <Edit className="w-4 h-4 mr-1" />
          {isEditing ? "Done Editing" : "Edit Permissions"}
        </Button>
      </div>

      {isEditing ? (
        <PermissionEditGrid
          role={role}
          permissions={permissions}
          onSavePermissions={onSavePermissions}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {Object.entries(permissions).map(([feature, actions]) => (
            <span 
              key={feature}
              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm"
            >
              <Check className="w-3 h-3 inline mr-1" />
              {feature.replace("_", " ")} ({Array.isArray(actions) ? actions.join(", ") : actions})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact Role Card for less common roles
function CompactRoleCard({
  role,
  title,
  description,
  permissions,
  editingRole,
  setEditingRole,
  onSavePermissions,
  colorClass,
  badgeClass
}) {
  const isEditing = editingRole === role;

  return (
    <div className={`border ${colorClass} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${badgeClass}`}>
            {title}
          </span>
          <span className="text-xs text-slate-500">{description}</span>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setEditingRole(isEditing ? null : role)}
        >
          <Edit className="w-4 h-4" />
        </Button>
      </div>
      {isEditing ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t mt-3">
          {Object.entries(permissions).map(([feature, actions]) => (
            <div key={feature} className="space-y-2">
              <p className="text-xs font-medium text-slate-600 capitalize">{feature.replace("_", " ")}</p>
              {["create", "read", "update", "delete", "send"].filter(a => 
                feature === "communications" ? ["read", "send"].includes(a) : 
                feature === "referrals" || feature === "clients" ? ["create", "read", "update"].includes(a) : 
                ["create", "read"].includes(a)
              ).map(action => (
                <div key={action} className="flex items-center gap-2">
                  <Switch
                    checked={Array.isArray(actions) && actions.includes(action)}
                    onCheckedChange={(checked) => {
                      const current = Array.isArray(actions) ? actions : [];
                      const updated = checked 
                        ? [...current, action] 
                        : current.filter(a => a !== action);
                      onSavePermissions(role, {
                        ...permissions,
                        [feature]: updated
                      });
                    }}
                  />
                  <span className="text-xs capitalize">{action}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {Object.entries(permissions).map(([feature]) => (
            <span key={feature} className={`px-2 py-0.5 rounded text-xs ${badgeClass.replace("text-", "bg-").replace("700", "50")} ${badgeClass}`}>
              {feature.replace("_", " ")}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Permission Edit Grid
function PermissionEditGrid({ role, permissions, onSavePermissions }) {
  const categories = [
    { key: "clients", label: "Client Access", icon: <Users className="w-4 h-4" />, actions: ["read", "update", "create", "delete"] },
    { key: "appointments", label: "Schedule Access", icon: <Clock className="w-4 h-4" />, actions: ["read", "update", "create", "delete"] },
    { key: "evv", label: "EVV & Time Tracking", icon: <MapPin className="w-4 h-4" />, actions: ["clock_in", "clock_out", "read", "update"] },
    { key: "visit_notes", label: "Visit Notes", icon: <FileText className="w-4 h-4" />, actions: ["create", "read", "update"] },
    { key: "communications", label: "Communications", icon: <Mail className="w-4 h-4" />, actions: ["read", "send"] },
    { key: "documents", label: "Documents / Files", icon: <FileText className="w-4 h-4" />, actions: ["read", "download", "upload", "delete"] },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4 border-t">
      {categories.map(({ key, label, icon, actions }) => (
        <div key={key} className="space-y-3">
          <h4 className="font-medium text-slate-700 flex items-center gap-2">
            {icon} {label}
          </h4>
          {actions.map(action => (
            <div key={action} className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <span className="text-sm capitalize">{action.replace("_", " ")}</span>
              <Switch
                checked={permissions[key]?.includes(action)}
                onCheckedChange={(checked) => {
                  const current = permissions[key] || [];
                  const updated = checked 
                    ? [...current, action] 
                    : current.filter(a => a !== action);
                  onSavePermissions(role, {
                    ...permissions,
                    [key]: updated
                  });
                }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Template Dialog Component
function TemplateDialog({ 
  open, 
  onOpenChange, 
  template, 
  setTemplate, 
  onSave, 
  saving, 
  togglePermission,
  isEditing 
}) {
  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditing ? <Edit className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-emerald-600" />}
            {isEditing ? `Edit Template: ${template.name}` : "Create Custom Permission Template"}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? "Modify your custom permission template" : "Build your own permission template to quickly configure roles"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Template Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                placeholder="e.g., Senior Caregiver"
                value={template.name}
                onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of this template"
                value={template.description || ""}
                onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                data-testid="template-description-input"
              />
            </div>
          </div>

          {/* Icon & Color Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                {TEMPLATE_ICONS.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setTemplate(prev => ({ ...prev, icon }))}
                    className={`w-8 h-8 rounded flex items-center justify-center text-lg transition-all ${
                      template.icon === icon 
                        ? 'bg-primary text-white shadow-md scale-110' 
                        : 'bg-white hover:bg-slate-100'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                {TEMPLATE_COLORS.map(color => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() => setTemplate(prev => ({ ...prev, color: color.id }))}
                    className={`w-8 h-8 rounded border-2 transition-all ${color.class} ${
                      template.color === color.id ? 'ring-2 ring-primary scale-110' : ''
                    }`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Permissions Grid */}
          <div className="space-y-2">
            <Label>Permissions</Label>
            <p className="text-xs text-slate-500 mb-3">Select the permissions to include in this template</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
              {Object.entries(PERMISSION_CATEGORIES).map(([category, config]) => (
                <div key={category} className="space-y-2">
                  <h5 className="font-medium text-sm text-slate-700">{config.label}</h5>
                  {config.actions.map(action => (
                    <div key={action} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`${isEditing ? 'edit' : 'new'}-${category}-${action}`}
                        checked={template.permissions?.[category]?.includes(action) || false}
                        onChange={() => togglePermission(category, action)}
                        className="rounded border-slate-300"
                      />
                      <label 
                        htmlFor={`${isEditing ? 'edit' : 'new'}-${category}-${action}`}
                        className="text-xs text-slate-600 capitalize cursor-pointer"
                      >
                        {action.replace("_", " ")}
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">{template.icon}</span>
              <div>
                <p className="font-medium text-slate-900">{template.name || "Template Name"}</p>
                <p className="text-xs text-slate-500">{template.description || "No description"}</p>
                <p className="text-xs text-emerald-600 mt-1">
                  {Object.keys(template.permissions || {}).length} categories, {' '}
                  {Object.values(template.permissions || {}).flat().length} permissions selected
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave} 
            disabled={saving || !template.name?.trim()}
            className={isEditing ? "" : "bg-emerald-600 hover:bg-emerald-700"}
            data-testid={isEditing ? "update-template-btn" : "save-template-btn"}
          >
            {saving ? "Saving..." : (isEditing ? "Save Changes" : "Create Template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Comparison Matrix Dialog
function ComparisonMatrixDialog({ open, onOpenChange, permissions }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Role Permissions Comparison Matrix
          </DialogTitle>
          <DialogDescription>
            Side-by-side comparison of all role permissions
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto flex-1 -mx-6 px-6">
          {permissions && (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-2 font-semibold text-slate-700 bg-slate-50 min-w-[180px]">Permission</th>
                  <th className="text-center py-3 px-2 min-w-[90px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Admin</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[90px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Case Mgr</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[90px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Intake</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[90px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">Reception</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[90px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">RN</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {permissions.permissions?.admin && Object.entries(permissions.permissions.admin).map(([feature, perms]) => {
                  const isSimple = typeof perms === "boolean";
                  const featureLabel = feature.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                  
                  if (isSimple) {
                    return (
                      <tr key={feature} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium text-slate-700">{featureLabel}</td>
                        {["admin", "case_manager", "intake_coordinator", "receptionist", "rn"].map((role) => {
                          const val = permissions.permissions[role]?.[feature];
                          return (
                            <td key={role} className="text-center py-2 px-2">
                              {val ? (
                                <span className="text-emerald-600">✓</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  } else {
                    return (
                      <tr key={feature} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium text-slate-700">{featureLabel}</td>
                        {["admin", "case_manager", "intake_coordinator", "receptionist", "rn"].map((role) => {
                          const rolePerms = permissions.permissions[role]?.[feature];
                          const count = Array.isArray(rolePerms) ? rolePerms.length : 0;
                          return (
                            <td key={role} className="text-center py-2 px-2">
                              {count > 0 ? (
                                <span className="text-emerald-600 font-bold">{count}</span>
                              ) : (
                                <span className="text-red-300">✗</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pt-4 border-t flex items-center justify-between">
          <div className="flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="text-emerald-600 font-bold">✓</span> Allowed</span>
            <span className="flex items-center gap-1"><span className="text-red-300">✗</span> Denied</span>
            <span className="flex items-center gap-1"><span className="text-slate-300">—</span> N/A</span>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
